/**
 * Shared compatibility hook and parser factories for nuqs.
 *
 * Provides shorthand factories so consumer files can declare query-param
 * schemas concisely:
 *
 *   import { useNuqsParams, intParam, stringParam } from "@/hooks/useNuqsParams";
 *
 *   const [query, setQuery] = useNuqsParams({
 *     page:       intParam(1),
 *     searchTerm: stringParam(""),
 *   });
 *
 * The setter returned by useNuqsParams (nuqs's useQueryStates) supports
 * both plain-object and updater-function signatures:
 *   setQuery({ page: 2 });
 *   setQuery(prev => ({ ...prev, page: prev.page + 1 }));
 */
import { useQueryStates, useQueryState, parseAsInteger, parseAsString, parseAsBoolean, parseAsArrayOf } from "nuqs";

// ---------------------------------------------------------------------------
// Shorthand parser factories – call with a default value to get a parser.
// ---------------------------------------------------------------------------

/** Integer query param – maps to `parseAsInteger.withDefault(defaultValue)` */
export const intParam = defaultValue => parseAsInteger.withDefault(defaultValue);

/** String query param – maps to `parseAsString.withDefault(defaultValue)` */
export const stringParam = defaultValue => parseAsString.withDefault(defaultValue);

/** Boolean query param – maps to `parseAsBoolean.withDefault(defaultValue)` */
export const boolParam = defaultValue => parseAsBoolean.withDefault(defaultValue);

/**
 * String-array query param – uses nuqs native comma-separated format.
 * URL example: `?loanTypes=BRIDGE,CONSTRUCTION`
 */
export const arrayParam = (defaultValue = []) => parseAsArrayOf(parseAsString).withDefault(defaultValue);

// ---------------------------------------------------------------------------
// Re-exports – for consumers that need the raw nuqs parsers directly.
// ---------------------------------------------------------------------------
export { parseAsInteger, parseAsString, parseAsBoolean, parseAsArrayOf };

// ---------------------------------------------------------------------------
// Hook aliases – thin wrappers so imports stay consistent across the app.
// ---------------------------------------------------------------------------

/** Multi-param hook (replaces `useQueryParams` from use-query-params). */
export const useNuqsParams = useQueryStates;

/** Single-param hook (replaces `useQueryParam` from use-query-params). */
export const useNuqsParam = useQueryState;
